import { estadosEvento, hotelesOpciones } from "../../hook/Arreglos";
import Swal from "sweetalert2";

import { insertartBitacoraEmpresa } from "../Empresa/thunkEmpresa";
import { insertartBitacoraCliente } from "../clientes/thunkClientes";
import { setLimpiarTemporales } from "./eventoSlice";
import {
    cancelar_EventosReasignacion,
    eliminar_EventosReasignacion,
    EXTARERTODOSOSVENTOSTOKEN,
    InsertarNuevoEvento,
    obtenerLosDatosEventos,
    validarEventosCalendario,
} from "./Api_Evento_Providers";
import {
    formatEventDateTime,
    normalizeEventDateRange,
    parseEventDateTime,
} from "./eventDateUtils";

/**
 * Formatea una fecha en un formato legible para el usuario.
 *
 * @param {Date|string|number} fecha - Fecha origen.
 * @returns {string} Fecha legible o `N/D`.
 */
const formatearFechaLegible = (fecha) => {
    const parsedDate = parseEventDateTime(fecha);

    if (!parsedDate) {
        return "N/D";
    }

    return parsedDate.toLocaleString("es-ES", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
    });
};

/**
 * Obtiene el nombre del hotel a partir de su ID.
 *
 * @param {string|number} idHotel - Identificador del hotel.
 * @returns {string} Nombre del hotel o `No encontrado`.
 */
const obtenerNombreHotel = (idHotel) =>
    hotelesOpciones.find((opcion) => opcion.value === idHotel?.toString())?.label || "No encontrado";

/**
 * Normaliza una lista de eventos antes de validarlos o persistirlos.
 *
 * @param {Array} eventosLista - Eventos origen.
 * @returns {{ normalizedEvents: Array, invalidEvents: Array }} Eventos válidos e inválidos.
 */
const normalizeEventsForPersistence = (eventosLista = []) => {
    return eventosLista.reduce(
        (accumulator, evento, index) => {
            const normalizedEvent = normalizeEventDateRange(evento);

            if (!normalizedEvent) {
                accumulator.invalidEvents.push({
                    index,
                    evento,
                });
                return accumulator;
            }

            accumulator.normalizedEvents.push(normalizedEvent);
            return accumulator;
        },
        {
            normalizedEvents: [],
            invalidEvents: [],
        },
    );
};

/**
 * Muestra un error cuando uno o más eventos tienen fechas inválidas.
 *
 * @param {Array} invalidEvents - Eventos inválidos detectados.
 * @returns {Promise<void>}
 */
const showInvalidDatesAlert = async (invalidEvents) => {
    const invalidRows = invalidEvents
        .map(({ evento, index }) => {
            const eventLabel = evento?.nombreSalon || evento?.title || `Evento #${index + 1}`;
            const rawStart = evento?.start || "sin fecha inicio";
            const rawEnd = evento?.end || "sin fecha final";

            return `<li><b>${eventLabel}</b>: ${rawStart} -> ${rawEnd}</li>`;
        })
        .join("");

    await Swal.fire({
        icon: "error",
        title: "Fechas inválidas detectadas",
        html: `
            <div style="text-align: left;">
                <p>No se guardó el evento porque hay fechas que no se pueden convertir a un formato válido.</p>
                <ul>${invalidRows}</ul>
            </div>
        `,
        confirmButtonText: "Entendido",
    });
};

/**
 * Recolecta metadatos del formulario para crear o editar un evento.
 *
 * @param {Object} formulario - Datos del formulario.
 * @returns {Object} Metadatos normalizados para persistencia.
 */
const buildEventMetadata = (formulario) => {
    const obtenerValoresFormateados = (propiedad, usarComillas = true) =>
        formulario.selectedExecutives?.map((ejecutivo) => (usarComillas ? `"${ejecutivo[propiedad]}"` : ejecutivo[propiedad])).join(",") ||
        "Ninguno";

    const estadoSeleccionado = estadosEvento.find(
        (estado) => estado.id_estado === parseInt(formulario.estadoEvento, 10),
    );

    return {
        cantidadPersonas: formulario.cantidadPersonas || 0,
        clienteSeleccionado: formulario.clienteSeleccionado?.value || 0,
        colorEstado: estadoSeleccionado?.color_estado || "Color no encontrado",
        empresaSeleccionada: formulario.empresaSeleccionada?.value || 0,
        etiquetasEjecutivos: obtenerValoresFormateados("label", false),
        idEstado: estadoSeleccionado?.id_estado || 0,
        montajeEvento: formulario.montajeEvento || "No especificado",
        monto: formulario.ingresoMonto || 0,
        nombreEvento: formulario.nombreEvento || "Sin nombre",
        notasEvento: formulario.notasEvento || "Sin notas",
        tipoEvento: formulario.tipodeeventos || "Sin tipo",
        valoresEjecutivos: obtenerValoresFormateados("value", true),
    };
};

/**
 * Valida conflictos y persiste una lista de eventos ya normalizada.
 *
 * @param {Object} params - Parámetros de persistencia.
 * @param {Function} params.dispatch - Dispatch de Redux.
 * @param {Array} params.normalizedEvents - Eventos normalizados.
 * @param {string} params.token - Token de la serie.
 * @param {Object} params.recolectarDaros - Metadatos del evento.
 * @param {string} params.nombreEvento - Nombre del evento principal.
 * @param {string} params.bitacoraNombreCliente - Etiqueta de bitácora cliente.
 * @param {string} params.bitacoraVerboEmpresa - Verbo usado en bitácora empresa.
 * @returns {Promise<string>} `ok` o `error`.
 */
const validateAndPersistEvents = async ({
    dispatch,
    normalizedEvents,
    token,
    recolectarDaros,
    nombreEvento,
    bitacoraNombreCliente,
    bitacoraVerboEmpresa,
}) => {
    const conflictosMensajes = [];

    Swal.fire({
        title: "Validando eventos...",
        text: "Por favor espera mientras se valida la información.",
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        },
    });

    for (const eventoRecorrer of normalizedEvents) {
        const fi = `${eventoRecorrer.start.split(" ")[0]} 00:00:00`;
        const fn = `${eventoRecorrer.end.split(" ")[0]} 23:59:59`;
        const hotelid = eventoRecorrer.hotelid;
        const salonid = eventoRecorrer.salonid;
        const respuestaValidation = await validarEventosCalendario({ hotelid, salonid, fi, fn });
        const fi2 = parseEventDateTime(eventoRecorrer.start);
        const fn2 = parseEventDateTime(eventoRecorrer.end);

        if (!fi2 || !fn2) {
            throw new Error(
                `No se pudo validar fechas para ${eventoRecorrer.title || eventoRecorrer.nombreSalon || "el evento"}`,
            );
        }

        const eventosConflicto = respuestaValidation.data.data.filter((evento) => {
            const inicioEvento = parseEventDateTime(evento.fech_Inicio_evento);
            const finEvento = parseEventDateTime(evento.fech_final_evento);

            if (!inicioEvento || !finEvento) {
                return false;
            }

            return (
                (fi2 > inicioEvento && fi2 < finEvento) ||
                (fn2 > inicioEvento && fn2 < finEvento) ||
                (inicioEvento >= fi2 && finEvento <= fn2)
            );
        });

        if (eventosConflicto.length === 0) {
            continue;
        }

        const conflictos = eventosConflicto
            .map((evento) => {
                return `
                    <div style="padding: 10px; border-bottom: 1px solid #ddd; margin-bottom: 10px;">
                        <p style="margin: 0;"><b>Evento Encontrado:</b> ${evento.nombre_evento}</p>
                        <p style="margin: 0;"><b>Salón:</b> ${eventoRecorrer.nombreSalon}</p>
                        <p style="margin: 0;"><b>Hotel:</b> ${obtenerNombreHotel(eventoRecorrer.hotelid)}</p>
                        <p style="margin: 0;"><b>Desde:</b> ${formatearFechaLegible(evento.fech_Inicio_evento)}</p>
                        <p style="margin: 0;"><b>Hasta:</b> ${formatearFechaLegible(evento.fech_final_evento)}</p>
                    </div>`;
            })
            .join("");

        conflictosMensajes.push(`
            <div style="text-align: left; margin-bottom: 15px;">
                <h3 style="margin-bottom: 10px; font-size: 18px; color: #e74c3c;">Conflicto en el salón: ${eventoRecorrer.nombreSalon}</h3>
                <p style="margin: 0;"><b>Desde :</b> ${formatearFechaLegible(eventoRecorrer.start)}</p>
                <p style="margin: 0; margin-bottom: 10px;"><b>Hasta:</b> ${formatearFechaLegible(eventoRecorrer.end)}</p>
                ${conflictos}
            </div>
        `);
    }

    Swal.close();

    if (conflictosMensajes.length > 0) {
        await Swal.fire({
            title: "Conflictos encontrados",
            icon: "error",
            html: `
                <div style="text-align: left; font-family: Arial, sans-serif; line-height: 1.5; font-size: 14px;">
                    <p style="font-weight: bold; margin-bottom: 15px; color: #333;">Los siguientes conflictos han sido detectados:</p>
                    <ul style="padding-left: 20px; list-style-type: disc;">
                        ${conflictosMensajes.map((mensaje) => `<li style="margin-bottom: 20px; padding: 10px; background: #f9f9f9; border: 1px solid #ddd; border-radius: 5px;">${mensaje}</li>`).join("")}
                    </ul>
                </div>`,
            width: "600px",
            confirmButtonText: "Entendido",
            customClass: {
                popup: "swal-wide",
            },
        });
        return "error";
    }

    await Promise.all(
        normalizedEvents.map((evento) => dispatch(insertarEvento(evento, token, recolectarDaros))),
    );

    if (recolectarDaros.idCliente > 0) {
        await dispatch(
            insertartBitacoraCliente({
                bitacora_cliente: recolectarDaros.idCliente,
                bitacora_nombreCliente: bitacoraNombreCliente,
                bitacora_detalle: `Se le ha generado un evento a este cliente con el nombre de: ${nombreEvento}`,
            }),
        );
    }

    if (recolectarDaros.idEmpresa > 0) {
        const nota = `Se le ha ${bitacoraVerboEmpresa} un evento a esta empresa con el nombre de: ${nombreEvento}`;
        const idEmpresa = recolectarDaros.idEmpresa;
        await dispatch(insertartBitacoraEmpresa(nota, idEmpresa));
    }

    dispatch(setLimpiarTemporales());
    return "ok";
};

async function generateTokenAsync() {
    function generarLetrasAleatorias() {
        const letras = "123ABCDEFGHIJKLMNOPQRSTUVWXY456789Zabcdefghijklmnopqrstuvwxyz";
        return Array.from({ length: 5 }, () => letras[Math.floor(Math.random() * letras.length)]).join("");
    }

    const header = btoa('{"alg":"HS256","typ":"JWT"}');
    const now = new Date();
    const payload = {
        date: now.toISOString(),
        randomString: Math.random().toString(36).substring(2, 10),
        timestamp: now.getTime(),
    };
    const base64Payload = btoa(JSON.stringify(payload));
    const signature = btoa(`${now.getTime()}${Math.random().toString(36).substring(2, 15)}`).substring(0, 25);

    return `${header}.${base64Payload}.${signature}-${generarLetrasAleatorias()}`;
}

/**
 * Crea uno o más eventos persistiendo fechas normalizadas.
 *
 * @param {Object} formulario - Datos del formulario del evento.
 * @param {Array} eventos - Lista de eventos a persistir.
 * @returns {Function} Thunk Redux.
 */
export const crearEvento = (formulario, eventos) => {
    console.clear();

    const metadata = buildEventMetadata(formulario);
    const eventosLista = eventos || [];

    return async (dispatch) => {
        try {
            const { normalizedEvents, invalidEvents } = normalizeEventsForPersistence(eventosLista);

            if (invalidEvents.length > 0) {
                await showInvalidDatesAlert(invalidEvents);
                return "error";
            }

            const token = await generateTokenAsync();
            const recolectarDaros = {
                ejecutivos: metadata.valoresEjecutivos,
                nombresEjecutivos: metadata.etiquetasEjecutivos,
                colorEvento: metadata.colorEstado,
                idEstadoEvento: metadata.idEstado,
                CantPersonas: metadata.cantidadPersonas,
                Montaje: metadata.montajeEvento,
                Notas: metadata.notasEvento,
                idEmpresa: metadata.empresaSeleccionada,
                idCliente: metadata.clienteSeleccionado,
                nombreEvento: metadata.nombreEvento,
                ingresoMonto: metadata.monto,
                tipoEvento: metadata.tipoEvento,
            };

            return await validateAndPersistEvents({
                dispatch,
                normalizedEvents,
                token,
                recolectarDaros,
                nombreEvento: metadata.nombreEvento,
                bitacoraNombreCliente: "Evento Asignado",
                bitacoraVerboEmpresa: "creado",
            });
        } catch (error) {
            console.error("Error al procesar el evento:", error);
            throw new Error("No se pudo crear el evento. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Inserta un evento en backend asegurando el formato final de fechas.
 *
 * @param {Object} evento - Evento a persistir.
 * @param {string} token - Token de la serie.
 * @param {Object} recolectarDaros - Metadatos del evento.
 * @returns {Function} Thunk Redux.
 */
export const insertarEvento = (evento, token, recolectarDaros) => {
    return async (dispatch, getState) => {
        const { id_admin } = getState().auth;
        const normalizedEvent = {
            ...evento,
            start: formatEventDateTime(evento.start),
            end: formatEventDateTime(evento.end),
        };

        if (!normalizedEvent.start || !normalizedEvent.end) {
            throw new Error("No se pudo insertar el evento porque sus fechas son inválidas.");
        }

        try {
            await InsertarNuevoEvento({
                evento: normalizedEvent,
                token,
                recolectarDaros,
                id_admin,
            });
        } catch (error) {
            console.error("Error al insertar el evento en el calendario:", error);
            throw new Error("No se pudo insertar el evento. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Obtiene datos del evento principal y sus relacionados.
 *
 * @param {number|string} eventoId - Identificador del evento.
 * @returns {Function} Thunk Redux.
 */
export const DatosEventos = (eventoId) => {
    return async () => {
        try {
            const resultado = await obtenerLosDatosEventos({ id: eventoId });
            return resultado.data;
        } catch (error) {
            console.error("Error al obtener los datos del evento:", error);
            throw new Error("No se pudo obtener el evento. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Edita una serie de eventos conservando el token existente.
 *
 * @param {Object} formulario - Datos del formulario.
 * @param {Array} eventos - Eventos nuevos temporales.
 * @returns {Function} Thunk Redux.
 */
export const editarEvento = (formulario, eventos) => {
    return async (dispatch) => {
        try {
            const eventosLista = [...formulario.listaEventosAsignados, ...eventos];
            return await dispatch(funcionEditar(formulario, eventosLista));
        } catch (error) {
            console.error("Error al editar el evento:", error);
            throw new Error("No se pudo editar el evento. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Ejecuta el flujo real de edición: borra reasignación previa y reinserta eventos normalizados.
 *
 * @param {Object} formulario - Datos del formulario.
 * @param {Array} eventos - Eventos a persistir.
 * @returns {Function} Thunk Redux.
 */
export const funcionEditar = (formulario, eventos) => {
    console.clear();

    const metadata = buildEventMetadata(formulario);
    const eventosLista = eventos || [];

    return async (dispatch) => {
        try {
            const { normalizedEvents, invalidEvents } = normalizeEventsForPersistence(eventosLista);

            if (invalidEvents.length > 0) {
                await showInvalidDatesAlert(invalidEvents);
                return "error";
            }

            const token = formulario.token;
            const recolectarDaros = {
                ejecutivos: metadata.valoresEjecutivos,
                nombresEjecutivos: metadata.etiquetasEjecutivos,
                colorEvento: metadata.colorEstado,
                idEstadoEvento: metadata.idEstado,
                CantPersonas: metadata.cantidadPersonas,
                Montaje: metadata.montajeEvento,
                Notas: metadata.notasEvento,
                idEmpresa: metadata.empresaSeleccionada,
                idCliente: metadata.clienteSeleccionado,
                nombreEvento: metadata.nombreEvento,
                ingresoMonto: metadata.monto,
                tipoEvento: metadata.tipoEvento,
                token: formulario.token,
            };

            await dispatch(eliminarEventosReasignacion(token));

            return await validateAndPersistEvents({
                dispatch,
                normalizedEvents,
                token,
                recolectarDaros,
                nombreEvento: metadata.nombreEvento,
                bitacoraNombreCliente: "Evento Editado",
                bitacoraVerboEmpresa: "editado",
            });
        } catch (error) {
            console.error("Error al procesar la edición del evento:", error);
            throw new Error("No se pudo editar el evento. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Elimina eventos por token de reasignación.
 *
 * @param {string} token - Token de la serie.
 * @returns {Function} Thunk Redux.
 */
export const eliminarEventosReasignacion = (token) => {
    return async () => {
        try {
            await eliminar_EventosReasignacion({ token });
        } catch (error) {
            console.error("Error al eliminar los eventos de reasignación:", error);
            throw new Error("No se pudieron eliminar los eventos de reasignación. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Cancela eventos por token de reasignación.
 *
 * @param {string} token - Token de la serie.
 * @returns {Function} Thunk Redux.
 */
export const cancelarEventosReasignacion = (token) => {
    return async () => {
        try {
            await cancelar_EventosReasignacion({ token });
        } catch (error) {
            console.error("Error al cancelar los eventos de reasignación:", error);
            throw new Error("No se pudieron cancelar los eventos de reasignación. Por favor, intente nuevamente.");
        }
    };
};

/**
 * Extrae todos los eventos asociados a un token.
 *
 * @param {string} token - Token de la serie.
 * @returns {Function} Thunk Redux.
 */
export const extarerEventoToken = (token) => {
    return async () => {
        try {
            const result = await EXTARERTODOSOSVENTOSTOKEN({ token });
            return result.data;
        } catch (error) {
            console.error("Error al obtener los eventos asociados al token:", error);
            throw new Error("No se pudieron obtener los eventos asociados. Por favor, intente nuevamente.");
        }
    };
};
